/**
 * Real Facebook comments, as already published per market on the marketing
 * site (messages/<locale>.json → onlineOrdering.socialProof) and sourced from
 * each brand's own Facebook page.
 *
 * Copied rather than fetched: the two apps share no runtime, and a review
 * strip that depends on a network call would leave a hole under the buy
 * button whenever the call is slow. Surnames stay abbreviated exactly as the
 * marketing site publishes them.
 *
 * A locale with no verified comments gets an empty list and the strip does
 * not render. Nothing here may be invented — cs, it, en and de-AT wait for
 * real material rather than borrowing another market's.
 */

export interface CanvasReview {
  /** Avatar letters, as published. */
  initials: string;
  /** Given name plus initial, never the full surname. */
  name: string;
  text: string;
}

export interface CanvasReviewSet {
  /** The market's own "real Facebook comments" heading. */
  title: string;
  items: CanvasReview[];
}

const REVIEWS: Record<string, CanvasReviewSet> = {
  "sl": {
    title: "RESNIČNI FACEBOOK KOMENTARJI",
    items: [
      {
        initials: "AL",
        name: "Andreja L.",
        text: "Sem naročila, hitra dostava in lepe fotografije 👏",
      },
      {
        initials: "DG",
        name: "Dijana G.",
        text: "Zelo sem zadovoljna. Naročilo sem poslala v nedeljo, dostavljeno pa je bilo v četrtek. Vse pohvale 👍",
      },
      {
        initials: "MP",
        name: "Marjeta P.",
        text: "Odlična kvaliteta fotografij in zelo hitra dostava! Priporočam 👍",
      },
    ],
  },
  "hr": {
    title: "PRAVI FACEBOOK KOMENTARI",
    items: [
      {
        initials: "MH",
        name: "Maja H.",
        text: "Naručila sam, brza dostava i lijepe fotografije 👏",
      },
      {
        initials: "IK",
        name: "Ivana K.",
        text: "Jako sam zadovoljna. Narudžbu sam poslala u nedjelju, a stigla je u četvrtak. Sve pohvale 👍",
      },
      {
        initials: "TR",
        name: "Tomislav R.",
        text: "Odlična kvaliteta fotografija i brza dostava! Preporučujem 👍",
      },
    ],
  },
  "sk": {
    title: "SKUTOČNÉ KOMENTÁRE NA FACEBOOKU",
    items: [
      {
        initials: "MK",
        name: "Marta K.",
        text: "Objednala som, rýchle doručenie a pekné fotografie 👏",
      },
      {
        initials: "PN",
        name: "Petra N.",
        text: "Som veľmi spokojná. Objednávku som odoslala v nedeľu, doručená bola vo štvrtok. Všetky pochvaly 👍",
      },
      {
        initials: "JH",
        name: "Jana H.",
        text: "Výborná kvalita fotografií a rýchle doručenie! Odporúčam 👍",
      },
    ],
  },
  "pl": {
    title: "PRAWDZIWE KOMENTARZE Z FACEBOOKA",
    items: [
      {
        initials: "MK",
        name: "Marta K.",
        text: "Zamówiłam, szybka dostawa i piękne zdjęcia 👏",
      },
      {
        initials: "AW",
        name: "Anna W.",
        text: "Bardzo zadowolona. Zamówiłam w niedzielę, dostałam w czwartek. Wszystko super 👍",
      },
      {
        initials: "KN",
        name: "Karolina N.",
        text: "Doskonała jakość zdjęć i szybka dostawa! Polecam 👍",
      },
    ],
  },
};

/**
 * The review set for a market, or null when none is verified for it.
 *
 * The locale may arrive as "sl" or as a fuller tag; only the language part
 * selects the set, and an unknown one answers null rather than falling back
 * to Slovenian — a Czech customer must not be shown Slovenian strangers.
 */
export function reviewsFor(locale: string): CanvasReviewSet | null {
  const lang = (locale || "").toLowerCase().split("-")[0];
  const set = REVIEWS[lang];
  return set && set.items.length > 0 ? set : null;
}
